import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { shareInfo, shareSendCode, shareVerifyCode, shareFileBlob } from '../data/legalApi';

// Secure share viewer (/share/:token) — a named outsider reads documents in
// the browser without an account:
//
//   1. Gate: what was shared, by whom, until when (live countdown). The
//      reader types their email; a one-time code goes out only if it is on
//      the link's recipient list, and the page can't tell which.
//   2. View: each PDF streamed into an in-page frame, toolbar hidden, with
//      the same countdown. The email gate and revocation are the control;
//      nothing is stamped into the pages. No download or print affordance
//      is offered here.
//
// No Firebase login, no Firestore — everything goes through the token-
// authenticated share API (backend/api/shareApi.js).

const fmtDate = (ms) => (ms ? new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '');

// "6 days 3 hours", "42 minutes", "expired" — re-rendered every minute.
const remaining = (ms, now) => {
  const left = (ms || 0) - now;
  if (left <= 0) return 'expired';
  const d = Math.floor(left / 86400000);
  const h = Math.floor((left % 86400000) / 3600000);
  const m = Math.floor((left % 3600000) / 60000);
  if (d > 0) return `${d} day${d === 1 ? '' : 's'} ${h} hour${h === 1 ? '' : 's'}`;
  if (h > 0) return `${h} hour${h === 1 ? '' : 's'} ${m} min`;
  return `${m} minute${m === 1 ? '' : 's'}`;
};

// The expiry line every screen shows: this link is temporary and the
// reader can see exactly how temporary.
function ExpiryLine({ expiresAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  const left = remaining(expiresAt, now);
  const soon = (expiresAt || 0) - now < 86400000;
  return (
    <div
      className="u-small"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-2)',
        padding: 'var(--sp-1) var(--sp-3)', borderRadius: 999,
        background: soon ? 'var(--warn-bg)' : 'var(--info-bg)',
        color: soon ? 'var(--warn)' : 'var(--info)',
      }}
    >
      <span aria-hidden>⏱</span>
      <span>Auto-expires in <strong>{left}</strong> · {fmtDate(expiresAt)}</span>
    </div>
  );
}

export default function SecureView() {
  const { token } = useParams();
  const [phase, setPhase] = useState('loading'); // loading | gate | view | blocked
  const [error, setError] = useState('');
  const [info, setInfo] = useState(null);
  const [email, setEmail] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [session, setSession] = useState('');
  const [files, setFiles] = useState([]);
  const [active, setActive] = useState(0);
  const [urls, setUrls] = useState({}); // n -> blob URL
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    shareInfo(token)
      .then((s) => { if (alive) { setInfo(s); setPhase('gate'); } })
      .catch((err) => { if (alive) { setError(err.message); setPhase('blocked'); } });
    return () => { alive = false; };
  }, [token]);

  // Fetch the active file once per session; revoke blob URLs on unmount.
  useEffect(() => {
    if (phase !== 'view' || urls[active] || !files[active]) return undefined;
    let alive = true;
    setBusy(true);
    shareFileBlob(token, active, session)
      .then((blob) => { if (alive) setUrls((u) => ({ ...u, [active]: URL.createObjectURL(blob) })); })
      .catch((err) => { if (alive) setError(err.message); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [phase, active, files, urls, token, session]);

  useEffect(() => () => { Object.values(urls).forEach((u) => URL.revokeObjectURL(u)); }, [urls]);

  const sendCode = async () => {
    setBusy(true);
    setError('');
    try {
      await shareSendCode(token, email.trim());
      setCodeSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await shareVerifyCode(token, email.trim(), code.trim());
      setSession(r.session);
      setFiles(r.files || []);
      setPhase('view');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const shell = (inner, wide) => (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)' }}>
      <div style={{ maxWidth: wide ? 1100 : 520, margin: '0 auto', padding: 'var(--sp-5)' }}>
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 'var(--sp-5)' }}>
          fast.legal <span className="u-muted" style={{ fontWeight: 400, fontSize: 13 }}>· secure share</span>
        </div>
        {inner}
      </div>
    </div>
  );

  if (phase === 'loading') return shell(<div className="cs-empty">Loading…</div>);

  if (phase === 'blocked') {
    return shell(
      <div className="cs-card" style={{ padding: 'var(--sp-5)' }}>
        <h2 style={{ marginTop: 0 }}>This link isn't available</h2>
        <p className="u-small u-muted">{error}</p>
        <p className="u-small u-muted">If you were expecting to read something, ask the sender for a fresh link.</p>
      </div>,
    );
  }

  if (phase === 'gate') {
    return shell(
      <div className="cs-card" style={{ padding: 'var(--sp-5)' }}>
        <h2 style={{ marginTop: 0 }}>{info.title}</h2>
        <p className="u-small u-muted" style={{ marginTop: 0 }}>
          {info.sender ? `${info.sender} shared ` : 'Shared: '}
          {info.files.length} document{info.files.length === 1 ? '' : 's'} with you. This link is temporary and the
          sender can withdraw it at any time.
        </p>
        <div style={{ margin: '0 0 var(--sp-3)' }}><ExpiryLine expiresAt={info.expiresAt} /></div>
        {info.note && <p className="u-small" style={{ whiteSpace: 'pre-wrap' }}>{info.note}</p>}
        <ul className="u-small u-muted" style={{ paddingLeft: 20 }}>
          {info.files.map((f, i) => <li key={i}>{f.name}</li>)}
        </ul>
        <div style={{ borderTop: '1px solid var(--border)', margin: 'var(--sp-4) 0', paddingTop: 'var(--sp-4)' }}>
          <p className="u-small" style={{ marginTop: 0 }}>
            To confirm it's you, enter the email address this link was shared with. We'll send a one-time code there.
          </p>
          {!codeSent ? (
            <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
              <input
                className="cs-input"
                style={{ width: 280 }}
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && emailOk) sendCode(); }}
              />
              <button className="cs-btn cs-btn-primary" disabled={busy || !emailOk} onClick={sendCode}>
                {busy ? 'Sending…' : 'Email me a code'}
              </button>
            </div>
          ) : (
            <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
              <p className="u-xs u-muted" style={{ margin: 0 }}>
                If <strong>{email.trim()}</strong> is on this link, a code is on its way. Check your inbox.
                {' '}<button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => { setCodeSent(false); setCode(''); }}>Change address</button>
              </p>
              <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
                <input
                  className="cs-input"
                  style={{ width: 160, fontSize: 20, letterSpacing: 6, textAlign: 'center' }}
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="······"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => { if (e.key === 'Enter' && code.length === 6) verify(); }}
                />
                <button className="cs-btn cs-btn-primary" disabled={busy || code.length !== 6} onClick={verify}>
                  {busy ? 'Checking…' : 'Verify'}
                </button>
                <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={busy} onClick={sendCode}>Resend</button>
              </div>
            </div>
          )}
          {error && <p className="u-small" style={{ color: 'var(--danger)' }}>{error}</p>}
        </div>
      </div>,
    );
  }

  // ---- phase 'view' ----------------------------------------------------------
  return shell(
    <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
      <div className="cs-card" style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
        <div className="u-split">
          <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
            <div>
              <strong>{info.title}</strong>
              <div className="u-xs u-muted">View only · confidential</div>
            </div>
            <ExpiryLine expiresAt={info.expiresAt} />
          </div>
          {files.length > 1 && (
            <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
              {files.map((f, i) => (
                <button
                  key={i}
                  className={`cs-btn cs-btn-sm ${i === active ? 'cs-btn-primary' : 'cs-btn-outline'}`}
                  onClick={() => setActive(i)}
                >
                  {f.name.replace(/\.pdf$/i, '')}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      {error && <p className="u-small" style={{ color: 'var(--danger)', margin: 0 }}>{error}</p>}
      <div className="cs-card" style={{ padding: 0, overflow: 'hidden', height: 'calc(100vh - 170px)', minHeight: 480 }}>
        {urls[active] ? (
          <iframe
            key={active}
            title={files[active]?.name || 'document'}
            src={`${urls[active]}#toolbar=0&navpanes=0&view=FitH`}
            style={{ width: '100%', height: '100%', border: 0 }}
          />
        ) : (
          <div className="cs-empty">{busy ? 'Loading document…' : ''}</div>
        )}
      </div>
    </div>,
    true,
  );
}
